import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Phone, 
  Play,
  Users,
  Megaphone,
  PhoneCall,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CallsPage() {
  const [contacts, setContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState(false);
  const [currentCall, setCurrentCall] = useState(null);
  const [showCallModal, setShowCallModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contactsRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/contacts?status=new`),
        axios.get(`${API}/campaigns`)
      ]);
      // Also include callback contacts
      const callbackRes = await axios.get(`${API}/contacts?status=callback`);
      setContacts([...contactsRes.data, ...callbackRes.data]);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const toggleContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const toggleAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.id));
    }
  };

  const startCall = async (contactId) => {
    if (!selectedCampaign) {
      toast.error("Bitte wählen Sie zuerst eine Kampagne");
      return;
    }

    const contact = contacts.find(c => c.id === contactId);
    setCurrentCall(contact);
    setShowCallModal(true);
    setCalling(true);

    try {
      const formData = new FormData();
      formData.append("contact_id", contactId);
      formData.append("campaign_id", selectedCampaign);

      await axios.post(`${API}/calls/start`, formData);
      toast.success(`Anruf an ${contact.name} gestartet`);
      
      // Refresh contacts after a delay
      setTimeout(() => {
        fetchData();
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Starten des Anrufs");
    } finally {
      setCalling(false);
    }
  };

  const startBulkCalls = async () => {
    if (!selectedCampaign) {
      toast.error("Bitte wählen Sie zuerst eine Kampagne");
      return;
    }
    if (selectedContacts.length === 0) {
      toast.error("Bitte wählen Sie mindestens einen Kontakt");
      return;
    }

    for (const contactId of selectedContacts) {
      await startCall(contactId);
      // Wait between calls
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    setSelectedContacts([]);
    toast.success("Alle Anrufe abgeschlossen");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "new":
        return <div className="w-2 h-2 rounded-full bg-blue-500" />;
      case "callback":
        return <Clock className="w-4 h-4 text-amber-500" />;
      case "success":
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case "rejected":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <div className="w-2 h-2 rounded-full bg-slate-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" data-testid="calls-title">
            Anrufe starten
          </h1>
          <p className="text-slate-500 mt-1">
            {contacts.length} Kontakte verfügbar
          </p>
        </div>
      </div>

      {/* Campaign Selection */}
      <Card className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)]">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-blue-600" />
            Kampagne auswählen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger className="w-full sm:w-80" data-testid="campaign-select">
                <SelectValue placeholder="Kampagne wählen..." />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((campaign) => (
                  <SelectItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedContacts.length > 0 && (
              <Button
                onClick={startBulkCalls}
                disabled={!selectedCampaign || calling}
                className="bg-emerald-600 hover:bg-emerald-700 btn-press"
                data-testid="start-bulk-calls-btn"
              >
                {calling ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {selectedContacts.length} Anrufe starten
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contacts Table */}
      <Card className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)] overflow-hidden">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Kontakte zum Anrufen
            </CardTitle>
            {contacts.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleAll}
                className="text-slate-600"
                data-testid="select-all-btn"
              >
                {selectedContacts.length === contacts.length ? "Alle abwählen" : "Alle auswählen"}
              </Button>
            )}
          </div>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table data-testid="calls-contacts-table">
            <TableHeader>
              <TableRow className="bg-slate-50/50 border-b border-slate-200">
                <TableHead className="w-12"></TableHead>
                <TableHead className="font-semibold text-slate-700">Name</TableHead>
                <TableHead className="font-semibold text-slate-700">Telefon</TableHead>
                <TableHead className="font-semibold text-slate-700">Status</TableHead>
                <TableHead className="font-semibold text-slate-700 text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <div className="h-12 bg-slate-100 animate-pulse rounded"></div>
                    </TableCell>
                  </TableRow>
                ))
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12">
                    <div className="empty-state">
                      <Phone className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500">Keine Kontakte zum Anrufen</p>
                      <p className="text-slate-400 text-sm mt-1">
                        Alle Kontakte wurden bereits angerufen oder abgelehnt
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow 
                    key={contact.id} 
                    className="table-row-hover border-b border-slate-100"
                    data-testid={`call-contact-row-${contact.id}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedContacts.includes(contact.id)}
                        onCheckedChange={() => toggleContact(contact.id)}
                        data-testid={`select-contact-${contact.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">
                      {contact.name}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {contact.phone}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(contact.status)}
                        <span className={`status-badge ${
                          contact.status === 'new' ? 'status-new' :
                          contact.status === 'callback' ? 'status-callback' :
                          contact.status === 'success' ? 'status-success' :
                          'status-rejected'
                        }`}>
                          {contact.status === 'new' ? 'Neu' :
                           contact.status === 'callback' ? 'Rückruf' :
                           contact.status === 'success' ? 'Erfolg' : 'Abgelehnt'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => startCall(contact.id)}
                        disabled={!selectedCampaign || calling}
                        className="bg-slate-900 hover:bg-slate-800 btn-press"
                        data-testid={`call-btn-${contact.id}`}
                      >
                        <Phone className="w-3.5 h-3.5 mr-1.5" />
                        Anrufen
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Call Modal */}
      <Dialog open={showCallModal} onOpenChange={setShowCallModal}>
        <DialogContent className="sm:max-w-md" data-testid="call-modal">
          <DialogHeader>
            <DialogTitle className="text-center">Anruf läuft</DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <PhoneCall className="w-10 h-10 text-emerald-600 animate-pulse" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900">
              {currentCall?.name}
            </h3>
            <p className="text-slate-500 mt-1">{currentCall?.phone}</p>
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Anruf wird durchgeführt...</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
